import {INFRA_TOKENS} from './di.infra.tokens';
import {DailyGameMocks} from '@/services/shared/DailyGameService/DailyGameService.mocks';
import {GameStatusMocks} from '@/services/shared/GameStatusService/GameStatusService.mocks';
import {LeaderBoardMocks} from '@/services/shared/LeaderBoardService/LeaderBoardService.mock';
import {TasksMocks} from '@/services/shared/TasksService/TasksService.mocks';
import {EventEmitter} from 'pixi.js';

import {Container} from 'brandi';

import {initLocalStorage} from '@specials/localstorage';
import {SentryBrowser} from '@specials/sentry-browser';

import {apiRoutes, clientRoutes} from '@/constants';
import {Environment, envs} from '@/envs';
import {ResourcesAll} from '@/resources';
import {ClickerIncomeUpdater} from '@/services/ClickerIncomeUpdater/ClickerIncomeUpdater';
import {CookieStorage} from '@/services/CookieStorage/CookieStorage';
import {RewardsAnimationsService} from '@/services/DailyRewardsAnimationService/RewardsAnimationsService';
import {DevToolsMbService} from '@/services/DevToolsMbService/DevToolsMbService';
import {ErrorsService} from '@/services/ErrorsService/ErrorsService';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {MediaService} from '@/services/MediaService/MediaService';
import {NavigationClickerModalService} from '@/services/NavigationClickerModalService/NavigationClickerModalService';
import {NavigationService} from '@/services/NavigationService/NavigationService';
import {PlatformService} from '@/services/PlatformService/PlatformService';
import {HttpClientProjectApi} from '@/services/ProjectApi/ProjectApiHttpClient';
import {ClickerStateMocks} from '@/services/ProjectApi/mocks/ClickerStateMocks';
import {ProjectDate} from '@/services/ProjectDate/ProjectDate';
import {QueryStringService} from '@/services/QueryStringService/QueryStringService';
import {ScrollService} from '@/services/ScrollService/ScrollService';
import {TracingService} from '@/services/TracingService/TracingService';
import {TrackingService} from '@/services/TrackingService/TrackingService';
import {DailyGameService} from '@/services/shared/DailyGameService/DailyGameService';
import {GameOperationsService} from '@/services/shared/GameOperationsService/GameOperationsService';
import {ImprovementsMapService} from '@/services/shared/ImprovementsMapService/ImprovementsMapService';
import {InitializeService} from '@/services/shared/InitializeService/InitializeService';
import {TasksService} from '@/services/shared/TasksService/TasksService';
import {Envs, GlobalWindow} from '@/types';
import {CallbackCollector} from '@/utils/CallbackCollector';
import {Vibration} from '@/utils/Vibration';
import {PseudoRandom} from '@/utils/random/PseudoRandom';
import {RealRandom} from '@/utils/random/RealRandom';

export function initInfraDeps(container: Container, resources: ResourcesAll): void {
  container.bind(INFRA_TOKENS.envs).toConstant(envs);
  container.bind(INFRA_TOKENS.resources).toConstant(resources);
  container.bind(INFRA_TOKENS.clientRoutes).toConstant(clientRoutes);
  container.bind(INFRA_TOKENS.apiRoutes).toConstant(apiRoutes);
  container.bind(INFRA_TOKENS.globalWindow).toConstant(globalThis.window as GlobalWindow);

  container.bind(INFRA_TOKENS.config).toConstant(configFactory(container.get(INFRA_TOKENS.envs)));

  container.bind(INFRA_TOKENS.httpClientProjectApi).toInstance(HttpClientProjectApi).inSingletonScope();
  container.bind(INFRA_TOKENS.scrollService).toInstance(ScrollService).inSingletonScope();
  container
    .bind(INFRA_TOKENS.sentryBrowser)
    .toInstance(() => SentryBrowser.getRealOrFake(container.get(INFRA_TOKENS.globalWindow)))
    .inSingletonScope();
  container.bind(INFRA_TOKENS.loggerService).toInstance(LoggerService).inSingletonScope();
  container.bind(INFRA_TOKENS.queryStringService).toInstance(QueryStringService).inSingletonScope();
  container.bind(INFRA_TOKENS.platformService).toInstance(PlatformService).inSingletonScope();

  container.bind(INFRA_TOKENS.trackingService).toInstance(TrackingService).inSingletonScope();
  container
    .bind(INFRA_TOKENS.localStorageService)
    .toInstance(() => initLocalStorage())
    .inSingletonScope();
  container.bind(INFRA_TOKENS.navigationService).toInstance(NavigationService).inSingletonScope();

  container.bind(INFRA_TOKENS.errorsService).toInstance(ErrorsService).inSingletonScope();

  container
    .bind(INFRA_TOKENS.navigationClickerModalService)
    .toInstance(NavigationClickerModalService)
    .inSingletonScope();

  container.bind(INFRA_TOKENS.initializeService).toInstance(InitializeService).inSingletonScope();

  container.bind(INFRA_TOKENS.dailyGameService).toInstance(DailyGameService).inSingletonScope();
  container.bind(INFRA_TOKENS.dailyGameMocks).toInstance(DailyGameMocks).inTransientScope();
  container.bind(INFRA_TOKENS.clickerStateMocks).toInstance(ClickerStateMocks).inTransientScope();
  container.bind(INFRA_TOKENS.leaderBoardMocks).toInstance(LeaderBoardMocks).inSingletonScope();
  container.bind(INFRA_TOKENS.gameStatusMocks).toInstance(GameStatusMocks).inSingletonScope();

  container.bind(INFRA_TOKENS.gameOperationsService).toInstance(GameOperationsService).inSingletonScope();

  container.bind(INFRA_TOKENS.tasksService).toInstance(TasksService).inSingletonScope();
  container.bind(INFRA_TOKENS.tasksMocks).toInstance(TasksMocks).inSingletonScope();

  container.bind(INFRA_TOKENS.realRandom).toInstance(RealRandom).inTransientScope();
  container.bind(INFRA_TOKENS.pseudoRandom).toInstance(PseudoRandom).inTransientScope();
  container.bind(INFRA_TOKENS.callbackCollector).toInstance(CallbackCollector).inTransientScope();
  container.bind(INFRA_TOKENS.cookieStorage).toInstance(CookieStorage).inSingletonScope();
  container.bind(INFRA_TOKENS.tracingService).toInstance(TracingService).inSingletonScope();

  container.bind(INFRA_TOKENS.devtoolsMbService).toInstance(DevToolsMbService).inSingletonScope();

  container.bind(INFRA_TOKENS.improvementsMapService).toInstance(ImprovementsMapService).inSingletonScope();

  container.bind(INFRA_TOKENS.mediaService).toInstance(MediaService).inSingletonScope();

  container.bind(INFRA_TOKENS.rewardsAnimationsService).toInstance(RewardsAnimationsService).inSingletonScope();
  container.bind(INFRA_TOKENS.eventEmitter).toInstance(EventEmitter).inTransientScope();
  container.bind(INFRA_TOKENS.vibration).toInstance(Vibration).inSingletonScope();
  container.bind(INFRA_TOKENS.clickerIncomeUpdater).toInstance(ClickerIncomeUpdater).inSingletonScope();
  container.bind(INFRA_TOKENS.projectDate).toInstance(ProjectDate).inSingletonScope();
}

/* Глушим eslint, потому что испольузем ReturnType<configFactory> в качестве типа для конфига в DI */
/* eslint-disable-next-line */
export function configFactory(envsData: Envs) {
  return {
    isLocal: envsData.APP_ENVIRONMENT === Environment.Local,
    isStage: envsData.APP_ENVIRONMENT === Environment.Stage,
    isProd: envsData.APP_ENVIRONMENT === Environment.Prod,
    staticUrl: envs.APP_STATIC_URL,
    cdnUrl: envs.APP_STATIC_BASE_URL,
  };
}
